/* ==========================================================================
   bundle_builder behaviour — the "Build Your Bundle" page.

   Holds the in-progress bundle client-side (up to `data-bundle-max` units),
   keeps the panel in sync (slot bars, counter, thumbnails, subtotal, reward
   milestones) and hands the finished bundle to the cart in ONE /cart/add.js
   call. Nothing is posted per card, so the visitor can build the whole mix
   before anything reaches the cart.

   Ported from shopify-html/js/bundle.js. Three things changed for the
   theme, all of them required by MUNCHIEF-HANDOFF.md §3:
     1. every [data-bundle] instance on the page is bound, not just the
        first, and the script re-inits on shopify:section:load;
     2. the static build's window.MunchiefCart handoff became /cart/add.js
        against Horizon's own cart;
     3. the panel renders the Figma "in progress" frame — dashed thumbnail
        tiles with a remove control and a quantity badge, and the card's Add
        pill turning into a quantity stepper — rather than the static
        build's text rows.
   ========================================================================== */
(function () {
  'use strict';

  var READY = 'munchiefBundleReady';

  /* Shopify money formatting, minus the dependency on the global Shopify
     object (which is not guaranteed on a section-only page). Values are in
     the shop's currency subunits, like every Shopify price field. */
  function formatMoney(cents, format) {
    var value = Math.round(cents || 0);
    var pattern = format || '{{amount}}';

    function withCommas(number) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }

    function withPeriods(number) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
    }

    function decimal(places, separator) {
      var fixed = (value / 100).toFixed(places);
      var parts = fixed.split('.');
      var whole = separator === '.' ? withPeriods(parts[0]) : withCommas(parts[0]);
      if (places === 0) return whole;
      return whole + (separator === '.' ? ',' : '.') + parts[1];
    }

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return decimal(0, ',');
        case 'amount_with_comma_separator':
          return decimal(2, '.');
        case 'amount_no_decimals_with_comma_separator':
          return decimal(0, '.');
        default:
          return decimal(2, ',');
      }
    });
  }

  function initSection(section) {
    if (section.dataset[READY] === 'true') return;
    section.dataset[READY] = 'true';

    /* 0 / unset = unlimited: no cap, no slot bar, no counter. */
    var MAX_UNITS = parseInt(section.dataset.bundleMax, 10) || Infinity;
    /* One row of the tile grid — keep in step with grid-template-columns in
       munchief-bundle-builder.css. */
    var PLACEHOLDER_ROW = 4;
    var moneyFormat = section.dataset.moneyFormat || '{{amount}}';
    var addUrl = section.dataset.cartAddUrl || '/cart/add.js';
    var cartUrl = section.dataset.cartUrl || '/cart';
    var afterAdd = section.dataset.afterAdd || 'cart';
    var requireFull = section.dataset.requireFull === 'true';
    var completeText = section.dataset.rewardsComplete || '';
    var removeIcon = section.dataset.removeIcon || '';

    var slots = section.querySelectorAll('.bundle-panel__slot');
    var count = section.querySelector('[data-bundle-count]');
    var note = section.querySelector('[data-bundle-note]');
    var list = section.querySelector('[data-bundle-items]');
    var subtotal = section.querySelector('[data-bundle-subtotal]');
    var submit = section.querySelector('[data-bundle-submit]');
    var fill = section.querySelector('[data-bundle-reward-fill]');
    var track = section.querySelector('.cart-rewards__track');
    var rewardMessage = section.querySelector('[data-bundle-reward-message]');

    if (!list || !submit) return;

    /* Phone dock: the chevron collapses the products drawer down to its title
       bar and the add button, and opens it back up. */
    var clearBtn = section.querySelector('[data-bundle-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        items = [];
        render();
      });
    }

    var dock = section.querySelector('.bundle-panel__dock');
    var dockToggle = section.querySelector('[data-bundle-dock-toggle]');
    if (dock && dockToggle) {
      dockToggle.addEventListener('click', function () {
        var collapsed = dock.classList.toggle('is-collapsed');
        dockToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      });
    }

    var emptyNote = note ? note.textContent : '';
    var submitLabel = submit.textContent.trim();

    /* One entry per chosen variant, in the order it was first added. */
    var items = [];

    function totalUnits() {
      return items.reduce(function (sum, item) { return sum + item.qty; }, 0);
    }

    function totalPrice() {
      return items.reduce(function (sum, item) { return sum + item.price * item.qty; }, 0);
    }

    function findItem(variantId) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].variantId === variantId) return items[i];
      }
      return null;
    }

    /* Card ---------------------------------------------------------------- */

    function readCard(card) {
      var select = card.querySelector('[data-bundle-variant]');
      var option = select && select.options[select.selectedIndex];

      /* Only a real variant id may enter the bundle. A card with no product
         picked renders a placeholder option, and everything downstream —
         the subtotal, the reward bar, the /cart/add.js payload — needs a
         number, not a label. */
      if (!option || !/^\d+$/.test(option.value)) return null;

      var image = card.querySelector('.product-card__media img');

      return {
        variantId: option.value,
        /* Stock left for this variant, when Shopify tracks it and stops
           selling at zero; absent means no limit to enforce. */
        max: option.dataset.max !== undefined ? parseInt(option.dataset.max, 10) : null,
        price: parseInt(option.dataset.price, 10) || 0,
        title: card.dataset.title || '',
        variantTitle: option.textContent.trim(),
        image: image ? image.getAttribute('src') : '',
        card: card,
        qty: 0
      };
    }

    function selectedVariantId(card) {
      var select = card.querySelector('[data-bundle-variant]');
      var option = select && select.options[select.selectedIndex];
      return option ? option.value : '';
    }

    function renderCard(card) {
      var item = findItem(selectedVariantId(card));
      var qty = item ? item.qty : 0;
      var addButton = card.querySelector('[data-bundle-add]');
      var stepper = card.querySelector('[data-bundle-qty]');
      var value = card.querySelector('[data-bundle-qty-value]');

      card.classList.toggle('is-selected', qty > 0);
      if (addButton) addButton.hidden = qty > 0;
      if (stepper) stepper.hidden = qty === 0;
      if (value) value.textContent = String(qty);
    }

    function renderCards() {
      section.querySelectorAll('[data-bundle-card]').forEach(renderCard);
    }

    /* Panel --------------------------------------------------------------- */

    function renderRewards() {
      if (!track || !fill) return;

      var milestones = Array.prototype.map.call(
        track.querySelectorAll('.cart-rewards__milestone'),
        function (node) {
          return {
            node: node,
            threshold: parseFloat(node.dataset.threshold) || 0,
            position: parseFloat(node.style.left) || 0,
            message: node.dataset.message || ''
          };
        }
      );

      if (!milestones.length) return;

      /* Thresholds are authored in whole currency units; the running total
         is in subunits. */
      var value = totalPrice() / 100;
      /* A crossed tier flips its circle to white with a black icon. */
      milestones.forEach(function (milestone) {
        var unlocked = value >= milestone.threshold;
        var price = milestone.node.querySelector('.cart-rewards__price');
        var view = milestone.node.querySelector('[data-bundle-gift-view]');

        milestone.node.classList.toggle('is-unlocked', unlocked);
        /* A crossed tier swaps its price for a View button. */
        if (price) price.hidden = unlocked;
        if (view) view.hidden = !unlocked;
      });

      var previous = { threshold: 0, position: 0 };
      var next = null;

      for (var i = 0; i < milestones.length; i++) {
        if (value < milestones[i].threshold) { next = milestones[i]; break; }
        previous = milestones[i];
      }

      var percent;
      if (!next) {
        percent = 100;
      } else {
        var span = next.threshold - previous.threshold;
        var progress = span > 0 ? (value - previous.threshold) / span : 0;
        percent = previous.position + (next.position - previous.position) * progress;
      }

      percent = Math.max(0.5, Math.min(100, percent));
      fill.style.width = percent + '%';
      track.setAttribute('aria-valuenow', String(Math.round(percent)));

      /* Keep the tier being worked toward in view in the swipeable bar. */
      var scroller = track.parentElement;
      if (scroller && scroller.scrollWidth > scroller.clientWidth) {
        var focus = (next || milestones[milestones.length - 1]).node;
        var offset = focus.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
        var targetLeft = scroller.scrollLeft + offset + focus.offsetWidth / 2 - scroller.clientWidth / 2;
        scroller.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      }

      if (!rewardMessage) return;

      if (!next) {
        rewardMessage.textContent = completeText;
        return;
      }

      var away = document.createElement('span');
      away.className = 'cart-rewards__amount';
      away.textContent = formatMoney((next.threshold - value) * 100, moneyFormat);

      /* Phones get a one-line title: the next tier's short name instead of the
         full "diary + coasters + pen + stickers" list. */
      var labelNode = next.node.querySelector('.cart-rewards__label');
      var short = window.matchMedia('(max-width: 768px)').matches && labelNode;
      var target = short ? 'a free ' + labelNode.textContent.replace(/^\s*\+\s*/, '').trim() : next.message;

      rewardMessage.textContent = 'You are ';
      rewardMessage.appendChild(away);
      rewardMessage.appendChild(document.createTextNode(' away from ' + target + '!'));
    }

    /* One tile per UNIT, not per line item: a bundle of two Chutney Pops
       shows two tiles, so the tile row reads as the bundle itself. Each
       tile's remove control takes one unit off, which keeps it consistent
       with the card's own stepper.

       Markup follows the Figma "mini card" frame (node 84:15514): a dashed
       tile holding the pack art whole, with the close control riding the
       top-right corner. No caption — the art is the label. */
    function renderThumb(item) {
      var tile = document.createElement('li');
      tile.className = 'bundle-panel__thumb';

      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        tile.appendChild(img);
      }

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bundle-panel__thumb-remove';
      remove.setAttribute('aria-label', 'Remove one ' + item.title + ' from your bundle');

      if (removeIcon) {
        var icon = document.createElement('img');
        icon.src = removeIcon;
        icon.alt = '';
        icon.width = 10;
        icon.height = 10;
        remove.appendChild(icon);
      }

      remove.addEventListener('click', function () {
        removeUnitById(item.variantId);
      });
      tile.appendChild(remove);

      return tile;
    }

    /* An unfilled slot is drawn as the same dashed tile carrying a plus, so
       the row always shows the whole bundle: what is in it and what is still
       missing. */
    function renderEmptyThumb() {
      var tile = document.createElement('li');
      tile.className = 'bundle-panel__thumb bundle-panel__thumb--empty';
      tile.setAttribute('aria-hidden', 'true');
      tile.innerHTML =
        '<svg class="bundle-panel__thumb-plus" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
        '<line x1="12" y1="5" x2="12" y2="19"></line>' +
        '<line x1="5" y1="12" x2="19" y2="12"></line></svg>';
      return tile;
    }

    function render() {
      var units = totalUnits();

      if (clearBtn) clearBtn.hidden = units === 0;

      slots.forEach(function (slot, index) {
        slot.classList.toggle('is-filled', index < units);
      });

      if (count) count.textContent = units + ' of ' + MAX_UNITS + ' selected';
      subtotal.textContent = formatMoney(totalPrice(), moneyFormat);

      /* With require_full on the button counts down to a complete bundle and
         only unlocks at the top, the way a fixed-size pack builder works.
         Off, any non-empty bundle can be added and the label never moves. */
      if (requireFull && MAX_UNITS !== Infinity) {
        var remaining = MAX_UNITS - units;
        submit.textContent = remaining > 0 ? 'Add ' + remaining + ' more' : submitLabel;
        submit.disabled = remaining > 0;
      } else {
        submit.textContent = submitLabel;
        submit.disabled = units === 0;
      }

      if (note) {
        note.hidden = units > 0;
        if (units === 0) note.textContent = emptyNote;
      }

      list.textContent = '';
      items.forEach(function (item) {
        for (var unit = 0; unit < item.qty; unit++) {
          list.appendChild(renderThumb(item));
        }
      });

      /* Placeholders only fill out the first row of four — not every unused
         slot. Ten empty tiles read as a chore and leave a ragged half-row;
         one tidy row of four says "start here" and quietly gives way as real
         products take its place. The bundle still holds max_items; the slot
         bars above are what track the full capacity. */
      for (var empty = units; empty < PLACEHOLDER_ROW; empty++) {
        list.appendChild(renderEmptyThumb());
      }

      renderCards();
      renderRewards();
      renderSheet();
    }

    /* State --------------------------------------------------------------- */

    function isFull() {
      return totalUnits() >= MAX_UNITS;
    }

    function announceFull() {
      if (!note) return;
      note.hidden = false;
      note.textContent = 'Bundle full — remove something to swap it out.';
    }

    function addUnit(card) {
      if (isFull()) {
        announceFull();
        return;
      }

      var item = readCard(card);
      if (!item) return;

      var existing = findItem(item.variantId);
      var inBundle = existing ? existing.qty : 0;

      /* Never let the bundle ask for more than is in stock — Shopify would
         reject the whole add. */
      if (item.max !== null && inBundle >= item.max) {
        if (note) {
          note.hidden = false;
          note.textContent = item.max > 0
            ? 'Only ' + item.max + ' of ' + item.title + ' (' + item.variantTitle + ') left in stock.'
            : item.title + ' (' + item.variantTitle + ') is sold out.';
        }
        return;
      }

      if (existing) {
        existing.qty += 1;
      } else {
        item.qty = 1;
        items.push(item);
      }

      render();
    }

    function removeUnit(card) {
      removeUnitById(selectedVariantId(card));
    }

    /* Takes one unit off from the panel side. The card's own stepper calls
       removeUnit(card); both end up here so the two controls cannot
       disagree about what is in the bundle. */
    function removeUnitById(variantId) {
      var item = findItem(variantId);
      if (!item) return;

      item.qty -= 1;
      if (item.qty <= 0) {
        items = items.filter(function (other) { return other !== item; });
      }

      render();
    }

    /* Card controls ------------------------------------------------------- */

    section.querySelectorAll('[data-bundle-card]').forEach(function (card) {
      var addButton = card.querySelector('[data-bundle-add]');
      var increase = card.querySelector('[data-bundle-increase]');
      var decrease = card.querySelector('[data-bundle-decrease]');
      var select = card.querySelector('[data-bundle-variant]');

      if (addButton) addButton.addEventListener('click', function () { addUnit(card); });
      if (increase) increase.addEventListener('click', function () { addUnit(card); });
      if (decrease) decrease.addEventListener('click', function () { removeUnit(card); });

      if (select) {
        select.addEventListener('change', function () {
          var option = select.options[select.selectedIndex];
          if (!option) return;

          /* Price row follows the chosen variant. */
          var sale = card.querySelector('[data-bundle-price-sale]');
          var compare = card.querySelector('[data-bundle-price-compare]');
          var save = card.querySelector('[data-bundle-price-save]');
          var price = parseInt(option.dataset.price, 10) || 0;
          var comparePrice = parseInt(option.dataset.compare, 10) || 0;

          if (sale && option.dataset.priceFormatted) sale.textContent = option.dataset.priceFormatted;

          if (compare) {
            compare.hidden = comparePrice <= price;
            if (!compare.hidden) compare.textContent = option.dataset.compareFormatted || '';
          }

          if (save) {
            save.hidden = comparePrice <= price;
            if (!save.hidden) save.textContent = formatMoney(comparePrice - price, moneyFormat) + ' OFF';
          }

          /* A card's units belong to the variant that was showing when they
             were added, so switching variant starts that variant at zero
             rather than silently re-pricing what is already in the bundle. */
          renderCard(card);
        });
      }
    });

    /* Gift sheet ---------------------------------------------------------- */

    var sheet = section.querySelector('[data-bundle-sheet]');
    var sheetPanel = sheet && sheet.querySelector('.bundle-sheet__panel');
    var sheetList = sheet && sheet.querySelector('[data-bundle-sheet-list]');
    var sheetTrigger = null;
    /* Thresholds of the tiers whose "Know more" is open. */
    var expanded = {};

    function capitalise(text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /* "a free tote + diary + pen" -> ["Tote", "Diary", "Pen"] */
    function giftItems(message) {
      return (message || '')
        .replace(/^\s*(an?\s+)?free\s+/i, '')
        .replace(/^\s*an?\s+/i, '')
        .split('+')
        .map(function (part) { return part.trim(); })
        .filter(Boolean)
        .map(capitalise);
    }

    /* One card per tier, read from the reward bar so the sheet always agrees
       with it: image, name, price, and whether the tier is unlocked yet. */
    function renderSheet() {
      if (!sheet || sheet.hidden) return;

      var value = totalPrice() / 100;
      sheetList.textContent = '';

      section.querySelectorAll('.cart-rewards__milestone').forEach(function (node) {
        var threshold = parseFloat(node.dataset.threshold) || 0;
        var unlocked = value >= threshold;
        var items = giftItems(node.dataset.message);
        var icon = node.querySelector('.cart-rewards__icon img');
        var price = node.querySelector('.cart-rewards__price');

        var card = document.createElement('li');
        card.className = 'gift-offer' + (unlocked ? ' is-unlocked' : '');

        var row = document.createElement('div');
        row.className = 'gift-offer__row';

        var thumb = document.createElement('span');
        thumb.className = 'gift-offer__thumb';
        if (icon) {
          var img = document.createElement('img');
          img.src = icon.getAttribute('src');
          img.alt = '';
          thumb.appendChild(img);
        }
        row.appendChild(thumb);

        var text = document.createElement('div');
        text.className = 'gift-offer__text';

        var name = document.createElement('p');
        name.className = 'gift-offer__name';
        name.textContent = items.join(' + ');
        text.appendChild(name);

        var status = document.createElement('p');
        status.className = 'gift-offer__status';
        status.textContent = unlocked
          ? 'Unlocked'
          : 'Shop for ' + formatMoney((threshold - value) * 100, moneyFormat) + ' more to unlock';
        text.appendChild(status);
        row.appendChild(text);

        var cost = document.createElement('span');
        cost.className = 'gift-offer__price';
        cost.textContent = price ? price.textContent : '';
        row.appendChild(cost);

        card.appendChild(row);

        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'gift-offer__more';
        more.setAttribute('aria-expanded', expanded[threshold] ? 'true' : 'false');
        more.textContent = 'Know more';
        card.appendChild(more);

        var details = document.createElement('ul');
        details.className = 'gift-offer__items';
        details.hidden = !expanded[threshold];
        items.forEach(function (itemText) {
          var li = document.createElement('li');
          li.textContent = itemText;
          details.appendChild(li);
        });
        card.appendChild(details);

        more.addEventListener('click', function () {
          expanded[threshold] = !expanded[threshold];
          renderSheet();
        });

        sheetList.appendChild(card);
      });
    }

    function closeSheet() {
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      document.documentElement.classList.remove('bundle-sheet-open');
      if (sheetTrigger && document.contains(sheetTrigger)) sheetTrigger.focus();
      sheetTrigger = null;
    }

    function openSheet(milestone, trigger) {
      if (!sheet) return;

      /* The tier that was tapped starts open; the rest stay collapsed. */
      expanded = {};
      expanded[parseFloat(milestone.dataset.threshold) || 0] = true;

      sheetTrigger = trigger;
      sheet.hidden = false;
      document.documentElement.classList.add('bundle-sheet-open');
      renderSheet();
      sheetPanel.focus();
    }

    if (sheet) {
      section.querySelectorAll('[data-bundle-gift-view]').forEach(function (button) {
        button.addEventListener('click', function () {
          openSheet(button.closest('.cart-rewards__milestone'), button);
        });
      });

      sheet.querySelectorAll('[data-bundle-sheet-close]').forEach(function (node) {
        node.addEventListener('click', closeSheet);
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeSheet();
      });
    }

    /* Cart handoff -------------------------------------------------------- */

    /* Free gifts: every unlocked tier names a gift variant in its
       data-gift-variant, and the gifts are cumulative (the top tier gets the
       whole kit). Unlocking is judged on snack spend alone, the same total
       the reward bar uses. */
    function unlockedGiftIds() {
      var value = totalPrice() / 100;
      var ids = [];

      section.querySelectorAll('.cart-rewards__milestone[data-gift-variant]').forEach(function (node) {
        var threshold = parseFloat(node.dataset.threshold) || 0;
        var id = node.dataset.giftVariant;
        if (value >= threshold && /^\d+$/.test(id)) ids.push(id);
      });

      return ids;
    }

    /* One gift set per order: skip any gift the cart already holds, and any
       variant that is already a bundle line. */
    function pendingGifts() {
      var ids = unlockedGiftIds().filter(function (id) { return !findItem(id); });
      if (!ids.length) return Promise.resolve([]);

      return fetch(cartUrl.replace(/\/cart\/?$/, '/') + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          var inCart = (cart.items || []).map(function (line) { return String(line.variant_id); });
          return ids.filter(function (id) { return inCart.indexOf(id) === -1; });
        })
        .catch(function () { return ids; });
    }

    submit.addEventListener('click', function () {
      if (!items.length) return;

      submit.classList.add('is-busy');

      function post(lines) {
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines })
        });
      }

      /* Gifts are worked out before the bundle lands in the cart, so the
         cart lookup cannot see the bundle's own lines. */
      var giftsPromise = pendingGifts();

      /* The bundle goes in first and on its own: /cart/add.js is
         all-or-nothing, so one sold-out gift must not sink the snacks. */
      post(items.map(function (item) {
        return { id: Number(item.variantId), quantity: item.qty };
      }))
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) {
              /* Shopify says exactly what went wrong — an out-of-stock line,
                 a variant that no longer exists — so show that rather than a
                 generic failure the visitor cannot act on. */
              var error = new Error(body.description || body.message || 'cart/add failed');
              error.shopifyMessage = body.description || body.message;
              throw error;
            }
            return body;
          });
        })
        .then(function () {
          /* Then each unlocked gift separately; a gift that is sold out is
             simply skipped. */
          return giftsPromise.then(function (giftIds) {
            return giftIds.reduce(function (chain, id) {
              return chain.then(function () {
                return post([{ id: Number(id), quantity: 1, properties: { _free_gift: 'true' } }])
                  .catch(function () {});
              });
            }, Promise.resolve());
          });
        })
        .then(function () {
          if (afterAdd === 'cart') {
            window.location.href = cartUrl;
            return;
          }
          items = [];
          render();
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          /* Show the bundle that was just added: the cart drawer refreshes
             itself and slides open. No-op if that section is not on the page. */
          document.dispatchEvent(new CustomEvent('munchief:cart:open', { bubbles: true }));
        })
        .catch(function (error) {
          if (note) {
            note.hidden = false;
            note.textContent =
              (error && error.shopifyMessage) || 'Could not add the bundle — please try again.';
          }
        })
        .then(function () {
          submit.classList.remove('is-busy');
        });
    });

    /* Category filter — the same `.filter-tabs` component collection_grid
       uses, so tab behaviour matches the shop-all page.

       The tab row arrives from Liquid holding only "All": a theme block
       exposes neither its type nor its settings to the parent section, so
       the section cannot read the group labels to render the rest. Each
       group carries its own data-category / data-filter-label, so the row
       is completed here from the groups themselves — a deleted group can
       never leave a dead tab behind. Same approach as
       munchief-collection-grid.js. */
    var tabRow = section.querySelector('[data-bundle-filters]');
    var groups = section.querySelectorAll('.bundle__group');

    if (tabRow) {
      groups.forEach(function (group) {
        var key = group.dataset.category;
        if (!key || key === 'all') return;
        if (tabRow.querySelector('[data-filter="' + key + '"]')) return;

        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'filter-tab';
        tab.dataset.filter = key;
        tab.setAttribute('aria-pressed', 'false');
        tab.textContent = group.dataset.filterLabel || key;
        tabRow.appendChild(tab);
      });
    }

    var tabs = section.querySelectorAll('[data-bundle-filters] .filter-tab');

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (other) {
          var isCurrent = other === tab;
          other.classList.toggle('is-active', isCurrent);
          other.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
        });

        groups.forEach(function (group) {
          group.hidden =
            tab.dataset.filter !== 'all' && group.dataset.category !== tab.dataset.filter;
        });
      });
    });

    render();
  }

  function init(root) {
    (root || document).querySelectorAll('[data-bundle]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
